import { Link, useNavigate, useLocation } from "react-router-dom";
import { Instagram, Mail, Phone, MapPin } from "lucide-react";
import logo from "@/assets/renew-santulan-logo.png";

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-card/80 backdrop-blur-sm border-t border-border mt-auto">
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Brand Section */}
          <div className="space-y-4">
            <img 
              src={logo} 
              alt="Renew Santulan" 
              className="h-20 w-auto"
            />
            <p className="text-muted-foreground text-sm leading-relaxed">
              Empowering your wellness journey through holistic nutrition and lifestyle coaching.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://instagram.com/renewsantulan" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="/#hero"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('hero');
                  }}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Home
                </a>
              </li>
              <li>
                <Link
                  to="/about"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  About Me
                </Link>
              </li>
              <li>
                <a
                  href="/#programs"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('programs');
                  }}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Programs
                </a>
              </li>
              <li>
                <a
                  href="/#testimonials"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('testimonials');
                  }}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Testimonials
                </a>
              </li>
              <li>
                <a
                  href="/#book-session"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('book-session');
                  }}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Book Consultation
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Services</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/programs#lifestyle"
                  onClick={() => {
                    setTimeout(() => {
                      document.getElementById('lifestyle')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm text-left"
                >
                  Lifestyle Diseases
                </Link>
              </li>
              <li>
                <Link
                  to="/programs#gut"
                  onClick={() => {
                    setTimeout(() => {
                      document.getElementById('gut')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm text-left"
                >
                  Gut Health
                </Link>
              </li>
              <li>
                <Link
                  to="/programs#hormonal"
                  onClick={() => {
                    setTimeout(() => {
                      document.getElementById('hormonal')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm text-left"
                >
                  Hormonal Balance
                </Link>
              </li>
              <li>
                <Link
                  to="/programs#inflammation"
                  onClick={() => {
                    setTimeout(() => {
                      document.getElementById('inflammation')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm text-left"
                >
                  Understanding Inflammation
                </Link>
              </li>
              <li>
                <Link
                  to="/programs#diabetes"
                  onClick={() => {
                    setTimeout(() => {
                      document.getElementById('diabetes')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm text-left"
                >
                  Diabetes Management
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Get in Touch</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <a 
                  href="mailto:renewsantulan@gmail.com" 
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  renewsantulan@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <a 
                  href="tel:+919910961228" 
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  +91 99109 61228
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground text-sm">
                  Gurgaon, India
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground text-sm text-center md:text-left">
              © {new Date().getFullYear()} YCD Nutrigenics LLP. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link
                to="/privacy-policy"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-muted-foreground hover:text-primary transition-colors text-sm"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-muted-foreground hover:text-primary transition-colors text-sm"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
